import { useGetEventParticipantHistory } from 'api/Event/Attendance'
import SelectDateFromGivenDates from 'components/global/SelectDateFromGivenDates'
import Spinner from 'components/Spinner'
import { convertToLocal, convertToUtc } from 'helper/utility'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import { eventDetailsStore } from 'state/event/eventDetails'
import { DirectCheckInOutResp } from 'types/event/eventAttendance'
import { EventResp } from 'types/event/events'
import HistoryTime from './HistoryComponents/HistoryTime'
import PersonalDetails from './HistoryComponents/PersonalDetails'
import HistoryStatus from './HistoryComponents/HistoryStatus'

const HistoryTab = () => {
  const { id }: { id: string } = useParams()
  const { participantData, selectedDate, setSelectedDate } =
    eventAttendanceStore(store => ({
      participantData: store.participantData,
      selectedDate: store.selectedDate,
      setSelectedDate: store.setSelectedDate,
    }))
  const eventDetails: EventResp = eventDetailsStore(
    store => store.eventDetailedData,
  )
  const [params, setParams] = useState({
    date: convertToUtc(selectedDate),
    eventId: id,
  })

  const {
    data: participantEventHistory,
    isLoading: isParticipantHistoryLoading,
    isFetching: isParticipantHistoryFetching,
    refetch: refetchParticipantHistory,
  } = useGetEventParticipantHistory(participantData._id, params)

  useEffect(() => {
    refetchParticipantHistory()
  }, [participantData._id, params])

  useMemo(() => {
    setParams(prev => ({ ...prev, date: convertToUtc(selectedDate) }))
  }, [selectedDate])

  if (isParticipantHistoryFetching || isParticipantHistoryLoading) {
    return (
      <div className="flex justify-center align-center py-40px w-full">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex justify-between gap-100px mt-20px">
      <div className="flex-1 overflow-auto">
        <h3 className="text-14 w-600">
          {moment(selectedDate).format('DD MMMM YYYY')}
        </h3>
        <div>
          <>
            {participantEventHistory.length ? (
              participantEventHistory.map((history: DirectCheckInOutResp) => {
                return (
                  <div className="border-btm pb-20px" key={history._id}>
                    {history.checkInType === 'request' ? (
                      <div className="mt-20px relative">
                        <h4 className={`text-12 w-600 text-prim`}>
                          Requester Check-in
                        </h4>
                        <div className="flex gap-20px align-center justify-between mt-10px">
                          <HistoryTime
                            time={history.checkInRequestedAt}
                            isCheckIn={true}
                            checkInId={history._id}
                          />

                          <PersonalDetails
                            title="Check-in requester"
                            user={history.checkInRequesterGuardin}
                          />
                        </div>
                      </div>
                    ) : null}

                    {history.checkInStatus === 'checked-in' ||
                    history.checkInStatus === 'droped-in' ? (
                      <div className="mt-20px relative">
                        <HistoryStatus status={history} isCheckIn={true} />
                        <div className="flex gap-20px align-center justify-between mt-10px">
                          <HistoryTime
                            time={history.checkedInAt}
                            isCheckIn={true}
                            checkInId={history._id}
                          />
                          {history.checkInAdmin && (
                            <PersonalDetails
                              title="Admin check-in"
                              user={history.checkInAdmin}
                            />
                          )}
                          {history.checkInGuardian && (
                            <PersonalDetails
                              title="Guardian check-in"
                              user={history.checkInGuardian}
                            />
                          )}
                          {history.checkInMentor && (
                            <PersonalDetails
                              title="Mentor check-in"
                              user={history.checkInMentor}
                            />
                          )}
                          {history.checkInOrganizer && (
                            <PersonalDetails
                              title="Organizer check-in"
                              user={history.checkInOrganizer}
                            />
                          )}
                        </div>
                      </div>
                    ) : null}

                    {history.checkOutRequestedAt ? (
                      <div className="mt-20px relative">
                        <h4 className={`text-12 w-600 text-prim`}>
                          Requester Check-out
                        </h4>
                        <div className="flex gap-20px align-center justify-between mt-10px">
                          <HistoryTime
                            time={history.checkOutRequestedAt}
                            isCheckIn={true}
                            checkInId={history._id}
                          />

                          <>
                            {history.checkOutRequesterAdmin && (
                              <PersonalDetails
                                title="Admin check-out requester"
                                user={history.checkOutRequesterAdmin}
                              />
                            )}
                            {history.checkOutRequesterMentor && (
                              <PersonalDetails
                                title="Mentor check-out requester"
                                user={history.checkOutRequesterMentor}
                              />
                            )}
                            {history.checkOutRequesterOrganizer && (
                              <PersonalDetails
                                title="Organizer check-out requester"
                                user={history.checkOutRequesterOrganizer}
                              />
                            )}
                          </>
                        </div>
                      </div>
                    ) : null}

                    {history.checkedOutAt && (
                      <div className="mt-20px relative">
                        <HistoryStatus status={history} isCheckIn={false} />
                        <div className="flex gap-20px align-center justify-between mt-10px">
                          <HistoryTime
                            time={history.checkedOutAt}
                            isCheckIn={false}
                            checkInId={history._id}
                          />
                          {history.checkOutAdmin && (
                            <PersonalDetails
                              title="Admin check-out"
                              user={history.checkOutAdmin}
                            />
                          )}
                          {history.checkOutGuardian && (
                            <PersonalDetails
                              title="Guardian check-out"
                              user={history.checkOutGuardian}
                            />
                          )}
                          {history.checkOutMentor && (
                            <PersonalDetails
                              title="Mentor check-out"
                              user={history.checkOutMentor}
                            />
                          )}
                          {history.checkOutOrganizer && (
                            <PersonalDetails
                              title="Organizer check-out"
                              user={history.checkOutOrganizer}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <p>No history found</p>
            )}
          </>
        </div>
      </div>

      <div>
        <SelectDateFromGivenDates
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          availableDates={eventDetails.datesOfEvent}
        />
      </div>
    </div>
  )
}

export default HistoryTab
